import { useLayoutEffect, useState, type RefObject } from "react";

import { max, min, scaleBand, scaleLinear, scaleLog } from "d3";

import { isTemporalCategoryLabel } from "@/lib/weekDates";

import type {
  CartesianChartSpec,
  ChartSeries,
  ChartValueFormat,
  ChartVariant,
} from "./types";

export interface VariantConfig {
  height: number;
  fontSize: number;
  yTicks: number;
  margin: { top: number; right: number; bottom: number; left: number };
}

export const VARIANT_CONFIG: Record<ChartVariant, VariantConfig> = {
  compact: {
    height: 180,
    fontSize: 10,
    yTicks: 4,
    margin: { top: 10, right: 12, bottom: 26, left: 40 },
  },
  default: {
    height: 240,
    fontSize: 11,
    yTicks: 5,
    margin: { top: 12, right: 16, bottom: 30, left: 48 },
  },
  hero: {
    height: 320,
    fontSize: 12,
    yTicks: 6,
    margin: { top: 16, right: 20, bottom: 34, left: 56 },
  },
};

/** Track a container's width via ResizeObserver so SVG charts stay responsive. */
export function useContainerWidth(
  ref: RefObject<HTMLElement | null>,
  fallback = 320,
): number {
  return useContainerSize(ref, { width: fallback, height: 0 }).width;
}

/** Track container width and height so dashboard tiles can resize charts in both axes. */
export function useContainerSize(
  ref: RefObject<HTMLElement | null>,
  fallback: { width: number; height: number },
): { width: number; height: number } {
  const [size, setSize] = useState(fallback);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const sync = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      setSize((prev) =>
        prev.width === rect.width && prev.height === rect.height
          ? prev
          : { width: rect.width, height: rect.height },
      );
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return size;
}

const compactFmt = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const fullFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

/** Short axis label form, e.g. 12.3k. */
export function formatCompact(value: number): string {
  return compactFmt.format(value);
}

/** Exact value form for tooltips, e.g. 12,340. */
export function formatFull(value: number): string {
  return fullFmt.format(value);
}

const usdFullFmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const intFullFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

/** Tooltip / readout form that respects a series' unit. */
export function formatSeriesValue(
  value: number,
  format?: ChartValueFormat,
): string {
  switch (format) {
    case "usd":
      return usdFullFmt.format(value);
    case "integer":
      return intFullFmt.format(value);
    case "percent":
      return `${fullFmt.format(value)}%`;
    default:
      return formatFull(value);
  }
}

/** Compact axis-tick form that respects a series' unit. */
export function formatAxisTick(
  value: number,
  format?: ChartValueFormat,
): string {
  const compact = formatCompact(value);
  if (format === "usd") return `$${compact}`;
  if (format === "percent") return `${compact}%`;
  return compact;
}

/** Which y-scale a series plots against. */
export function seriesYAxis(series: Pick<ChartSeries, "yAxis">): "left" | "right" {
  return series.yAxis === "right" ? "right" : "left";
}

/** True when at least one series is assigned to the right axis. */
export function hasDualYAxis(spec: CartesianChartSpec): boolean {
  return spec.series.some((s) => s.yAxis === "right");
}

export interface CartesianModel {
  width: number;
  height: number;
  innerWidth: number;
  innerHeight: number;
  margin: VariantConfig["margin"];
  fontSize: number;
  /** Ordered unique category labels across all series. */
  categories: string[];
  /** Band scale over categories (vertical layout: horizontal axis). */
  x: ReturnType<typeof scaleBand<string>>;
  /** Value scale (vertical layout: vertical axis). */
  y: (value: number) => number;
  /** Plot-pixel baseline bars grow from (y(0) on linear, chart floor on log). */
  yBaseline: number;
  yTickValues: number[];
  /** True when the value axis is logarithmic (zeros / negatives are not plotted). */
  logY: boolean;
  /** Every nth category label to render, chosen so labels don't overlap. */
  labelStep: number;
  /** Right-axis scale when a mixed chart plots a second unit. */
  y2?: (value: number) => number;
  y2Baseline?: number;
  y2TickValues?: number[];
  yFormat?: ChartValueFormat;
  y2Format?: ChartValueFormat;
  /** Categories on Y and values on X (horizontal bars/lines). */
  horizontal: boolean;
  /** Band scale over categories when `horizontal` (maps to Y). */
  yBand?: ReturnType<typeof scaleBand<string>>;
  /** Value scale when `horizontal` (maps to X). */
  xValue?: (value: number) => number;
  xValueBaseline?: number;
  xValueTickValues?: number[];
}

const ISO_WEEK_LABEL = /^W(\d{1,2})-(\d{4})$/;

function sortCategories(labels: string[]): string[] {
  if (labels.length === 0 || !labels.every((label) => ISO_WEEK_LABEL.test(label))) {
    return labels;
  }
  return [...labels].sort((a, b) => {
    const ma = a.match(ISO_WEEK_LABEL);
    const mb = b.match(ISO_WEEK_LABEL);
    if (!ma || !mb) return a.localeCompare(b);
    const yearCmp = Number(ma[2]) - Number(mb[2]);
    return yearCmp !== 0 ? yearCmp : Number(ma[1]) - Number(mb[1]);
  });
}

function categoryHasPlottedValue(
  spec: CartesianChartSpec,
  category: string,
): boolean {
  return spec.series.some((series) =>
    series.data.some(
      (point) => point.x === category && Number.isFinite(point.y) && point.y !== 0,
    ),
  );
}

/**
 * Ordered union of category (x) labels across all series.
 * ISO week labels sort chronologically. Categorical bar charts drop all-zero
 * columns; time-series bars keep empty weeks/months so the date window stays
 * continuous.
 */
export function collectCategories(spec: CartesianChartSpec): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const series of spec.series) {
    for (const point of series.data) {
      if (!seen.has(point.x)) {
        seen.add(point.x);
        ordered.push(point.x);
      }
    }
  }
  const sorted = sortCategories(ordered);
  const temporal = sorted.length > 0 && sorted.every(isTemporalCategoryLabel);
  if (spec.type === "bar" && !temporal) {
    return sorted.filter((cat) => categoryHasPlottedValue(spec, cat));
  }
  return sorted;
}

/** True when grouped bar series should render as a single stacked column. */
export function isStackedBarChart(spec: CartesianChartSpec): boolean {
  return (
    spec.type === "bar" &&
    spec.stacked === true &&
    spec.series.length > 1
  );
}

/**
 * Values that drive the y-axis domain. Stacked bars use per-category totals
 * (matching the email/dashboard renderer) so the column height fits the sum.
 */
export function collectScaleValues(
  spec: CartesianChartSpec,
  focusSeries?: number | null,
): number[] {
  if (isStackedBarChart(spec) && focusSeries == null) {
    const categories = collectCategories(spec);
    const values: number[] = [];
    for (const cat of categories) {
      let stackSum = 0;
      for (const series of spec.series) {
        const point = series.data.find((p) => p.x === cat);
        if (point == null || !Number.isFinite(point.y)) continue;
        stackSum += point.y;
        values.push(point.y);
      }
      values.push(stackSum);
    }
    return values;
  }

  const values: number[] = [];
  spec.series.forEach((series, i) => {
    if (focusSeries != null && i !== focusSeries) return;
    for (const point of series.data) values.push(point.y);
  });
  return values;
}

/** Values that drive one side of a dual-axis mixed chart. */
export function collectScaleValuesForAxis(
  spec: CartesianChartSpec,
  axis: "left" | "right",
  focusSeries?: number | null,
): number[] {
  const values: number[] = [];
  spec.series.forEach((series, i) => {
    if (focusSeries != null && i !== focusSeries) return;
    if (seriesYAxis(series) !== axis) return;
    for (const point of series.data) values.push(point.y);
  });
  return values;
}

function firstFormatOnAxis(
  spec: CartesianChartSpec,
  axis: "left" | "right",
): ChartValueFormat | undefined {
  if (spec.type === "mixed") {
    const explicit = axis === "right" ? spec.y2Format : spec.yFormat;
    if (explicit) return explicit;
  }
  return spec.series.find((s) => seriesYAxis(s) === axis)?.valueFormat;
}

/** True when categories plot on Y and values on X. Mixed charts never swap. */
export function isHorizontalChart(spec: CartesianChartSpec): boolean {
  return spec.horizontal === true && spec.type !== "mixed";
}

/** Fit plot height and margins to the measured tile body (short tiles tighten chrome). */
function resolveCartesianLayout(
  containerHeight: number,
  cfg: VariantConfig,
  horizontal: boolean,
): { height: number; margin: VariantConfig["margin"]; yTicks: number } {
  if (containerHeight <= 0) {
    const margin = horizontal ? swapHorizontalMargins(cfg.margin) : cfg.margin;
    return { height: cfg.height, margin, yTicks: cfg.yTicks };
  }

  const height = containerHeight;
  let margin = horizontal ? swapHorizontalMargins(cfg.margin) : cfg.margin;
  let innerHeight = height - margin.top - margin.bottom;

  if (innerHeight < 24) {
    const base = horizontal ? swapHorizontalMargins(cfg.margin) : cfg.margin;
    margin = {
      top: Math.max(4, Math.round(base.top * 0.5)),
      right: Math.max(8, Math.round(base.right * 0.75)),
      bottom: Math.max(14, Math.round(base.bottom * 0.65)),
      left: Math.max(28, Math.round(base.left * 0.75)),
    };
    innerHeight = height - margin.top - margin.bottom;
  }

  const yTicks =
    innerHeight < 40 ? 2 : innerHeight < 56 ? 3 : innerHeight < 96 ? 4 : cfg.yTicks;

  return { height, margin, yTicks };
}

/** Reserve left space for category labels and bottom space for value ticks. */
function swapHorizontalMargins(margin: VariantConfig["margin"]): VariantConfig["margin"] {
  return {
    top: margin.top,
    right: margin.right,
    bottom: Math.max(margin.left, 36),
    left: Math.max(margin.bottom + 12, 56),
  };
}

/**
 * Build shared cartesian scales + layout for bar / line / mixed charts.
 * D3 owns the math; the chart components render the SVG marks from these.
 *
 * `focusSeries` keeps the full x-axis (all categories) but zooms the y-scale
 * to a single series so isolating a small line does not stay crushed at the
 * bottom of a taller neighbour.
 */
export function useCartesianModel(
  spec: CartesianChartSpec,
  variant: ChartVariant,
  ref: RefObject<HTMLElement | null>,
  focusSeries?: number | null,
): CartesianModel {
  const cfg = VARIANT_CONFIG[variant];
  const container = useContainerSize(ref, { width: 320, height: cfg.height });
  const width = container.width;
  const dualY = hasDualYAxis(spec);
  const horizontal = isHorizontalChart(spec);
  const layout = resolveCartesianLayout(container.height, cfg, horizontal);
  const height = layout.height;
  const yTicks = layout.yTicks;
  const margin = dualY
    ? {
        ...layout.margin,
        right: Math.max(layout.margin.right, layout.margin.left),
      }
    : layout.margin;
  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);

  const categories = collectCategories(spec);

  const leftValues = dualY
    ? collectScaleValuesForAxis(spec, "left", focusSeries)
    : collectScaleValues(spec, focusSeries);
  const rightValues = dualY
    ? collectScaleValuesForAxis(spec, "right", focusSeries)
    : [];

  if (horizontal) {
    const yBand = scaleBand<string>()
      .domain(categories)
      .range([0, innerHeight])
      .paddingInner(0.22)
      .paddingOuter(0.06)
      .align(0.5);

    const valueScale = buildValueScale(
      leftValues,
      innerWidth,
      yTicks,
      spec.yScale === "log",
      "horizontal",
    );

    const maxLabels = Math.max(1, Math.floor(innerHeight / 20));
    const labelStep = Math.max(1, Math.ceil(categories.length / maxLabels));

    const x = scaleBand<string>().domain([]).range([0, 0]);
    const y = () => 0;

    return {
      width,
      height,
      innerWidth,
      innerHeight,
      margin,
      fontSize: cfg.fontSize,
      categories,
      x,
      y,
      yBaseline: 0,
      yTickValues: [],
      logY: valueScale.logY,
      labelStep,
      horizontal: true,
      yBand,
      xValue: valueScale.y,
      xValueBaseline: valueScale.yBaseline,
      xValueTickValues: valueScale.yTickValues,
      yFormat: firstFormatOnAxis(spec, "left"),
    };
  }

  const x = scaleBand<string>()
    .domain(categories)
    .range([0, innerWidth])
    .paddingInner(0.22)
    .paddingOuter(0.06)
    .align(0.5);

  const valueScale = buildValueScale(
    leftValues,
    innerHeight,
    yTicks,
    spec.yScale === "log",
  );
  const rightScale = dualY
    ? buildValueScale(rightValues, innerHeight, yTicks, false)
    : null;

  // Keep ~64px between rendered labels so they don't collide.
  const maxLabels = Math.max(1, Math.floor(innerWidth / 64));
  const labelStep = Math.max(1, Math.ceil(categories.length / maxLabels));

  return {
    width,
    height,
    innerWidth,
    innerHeight,
    margin,
    fontSize: cfg.fontSize,
    categories,
    x,
    y: valueScale.y,
    yBaseline: valueScale.yBaseline,
    yTickValues: valueScale.yTickValues,
    logY: valueScale.logY,
    labelStep,
    horizontal: false,
    ...(rightScale
      ? {
          y2: rightScale.y,
          y2Baseline: rightScale.yBaseline,
          y2TickValues: rightScale.yTickValues,
        }
      : {}),
    yFormat: firstFormatOnAxis(spec, "left"),
    y2Format: dualY ? firstFormatOnAxis(spec, "right") : undefined,
  };
}

/**
 * Linear scale from a zero-padded domain, or a log scale when requested and
 * at least one value is strictly positive. Log domains start at 1 for
 * integer-like data so small series still have visible height.
 */
export function buildValueScale(
  values: number[],
  plotExtent: number,
  tickCount: number,
  wantLog: boolean,
  orientation: "vertical" | "horizontal" = "vertical",
): {
  y: (value: number) => number;
  yBaseline: number;
  yTickValues: number[];
  logY: boolean;
} {
  const positives = values.filter((v) => Number.isFinite(v) && v > 0);
  const useLog = wantLog && positives.length > 0;
  const range =
    orientation === "horizontal" ? [0, plotExtent] : [plotExtent, 0];

  if (useLog) {
    const dataMax = max(positives) ?? 1;
    const dataMin = min(positives) ?? 1;
    const domainMin = dataMin >= 1 ? 1 : 10 ** Math.floor(Math.log10(dataMin));
    const domainMax = dataMax <= domainMin ? domainMin * 10 : dataMax;
    const scale = scaleLog().domain([domainMin, domainMax]).range(range);
    return {
      y: (value: number) => scale(value),
      yBaseline: orientation === "horizontal" ? 0 : plotExtent,
      yTickValues: logTickValues(domainMin, domainMax, tickCount),
      logY: true,
    };
  }

  const dataMax = max(values) ?? 0;
  const dataMin = min(values) ?? 0;
  const domainMax = Math.max(0, dataMax);
  const domainMin = Math.min(0, dataMin);
  const scale = scaleLinear()
    .domain([domainMin, domainMax === domainMin ? domainMax + 1 : domainMax])
    .nice(tickCount)
    .range(range);
  return {
    y: (value: number) => scale(value),
    yBaseline: scale(0),
    yTickValues: scale.ticks(tickCount),
    logY: false,
  };
}

/** Decade ticks (1–2–5 when the range is short, powers of 10 otherwise). */
function logTickValues(domainMin: number, domainMax: number, maxTicks: number): number[] {
  const ticks: number[] = [];
  const startExp = Math.floor(Math.log10(domainMin) - 1e-12);
  const endExp = Math.ceil(Math.log10(domainMax) + 1e-12);
  for (let e = startExp; e <= endExp; e++) {
    const base = 10 ** e;
    for (const m of [1, 2, 5]) {
      const v = base * m;
      if (v >= domainMin * (1 - 1e-9) && v <= domainMax * (1 + 1e-9)) {
        ticks.push(v);
      }
    }
  }
  if (ticks.length === 0) return [domainMin, domainMax];
  if (ticks.length <= maxTicks) return ticks;
  const decades = ticks.filter((v) => {
    const r = Math.log10(v);
    return Math.abs(r - Math.round(r)) < 1e-8;
  });
  return decades.length > 0 ? decades : ticks.filter((_, i) => i % 2 === 0);
}
